import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

interface ScoreDisplayProps {
  score: number;
  streak?: number;
}

const ScoreContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const ScoreItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const ScoreLabel = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
`;

const ScoreValue = styled.span`
  font-size: 1.75rem;
  font-weight: 700;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.accent}, ${({ theme }) => theme.colors.primary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-variant-numeric: tabular-nums;
`;

const StreakValue = styled(ScoreValue)`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.success}, ${({ theme }) => theme.colors.info});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const StreakIcon = styled.span`
  font-size: 1.25rem;
  filter: grayscale(0);
  -webkit-text-fill-color: initial;
`;

const StreakDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const AnimatedScore: React.FC<{ value: number }> = ({ value }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const duration = 500; // Animation duration in ms
    const startTime = Date.now();
    const startValue = displayValue;
    const diff = value - startValue;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out function
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(startValue + diff * easeOut);

      setDisplayValue(current);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return <ScoreValue>{displayValue}</ScoreValue>;
};

const ScoreDisplay: React.FC<ScoreDisplayProps> = ({ score, streak }) => {
  const [prevScore, setPrevScore] = useState(score);
  const [scoreChanged, setScoreChanged] = useState(false);

  useEffect(() => {
    if (score !== prevScore) {
      setScoreChanged(true);
      setPrevScore(score);
      const timer = setTimeout(() => setScoreChanged(false), 600);
      return () => clearTimeout(timer);
    }
  }, [score, prevScore]);

  return (
    <ScoreContainer>
      <ScoreItem
        as={motion.div}
        animate={scoreChanged ? { scale: [1, 1.15, 1] } : {}}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <ScoreLabel>Score</ScoreLabel>
        <AnimatedScore value={score} />
      </ScoreItem>
      {streak !== undefined && streak > 0 && (
        <ScoreItem
          as={motion.div}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        >
          <ScoreLabel>Streak</ScoreLabel>
          <StreakDisplay>
            <StreakValue
              as={motion.span}
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 0.3, repeat: streak > 3 ? Infinity : 0, repeatDelay: 1 }}
            >
              {streak}
            </StreakValue>
            <StreakIcon
              as={motion.span}
              animate={streak > 3 ? { rotate: [0, -10, 10, -10, 0] } : {}}
              transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 0.5 }}
            >
              🔥
            </StreakIcon>
          </StreakDisplay>
        </ScoreItem>
      )}
    </ScoreContainer>
  );
};

export default ScoreDisplay;
